import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { fetchSubTasksByTaskId, deleteSubTask } from "./subTaskService";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useResizableColumns } from "./issues/useResizableColumns";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import "../assets/css/Projects.css";
import "../assets/css/ViewCustomer.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";
import "../assets/css/DigixLeads.css";

const SUBTASKS_ROWS_PER_PAGE_KEY = "subtasksRowsPerPage";

function SubTasksTable({
  taskId,
  storyId,
  projectId,
  subProjectId,
  taskTitle,
  storyTitle,
  projectName,
  subProjectName,
  onRefresh
}) {
  const navigate = useNavigate();
  const [subTasks, setSubTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [subTaskPage, setSubTaskPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(SUBTASKS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return 5;
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [titleSearch, setTitleSearch] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const SUBTASK_COL_DEFAULT_WIDTHS = [180, 260, 120, 130, 120, 120, 150, 130, 110];
  const { getColProps, ResizeHandle } = useResizableColumns(SUBTASK_COL_DEFAULT_WIDTHS);

  const TASK_PRIORITIES = ["High", "Medium", "Low"];

  const loadSubTasks = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const statusId = statusFilter && /^\d+$/.test(statusFilter) ? Number(statusFilter) : null;
      const priority = priorityFilter ? String(priorityFilter).trim() : null;
      const data = await fetchSubTasksByTaskId(taskId, statusId, priority);
      setSubTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching subtasks:", err);
      setSubTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, statusFilter, priorityFilter]);

  const filteredSubTasks = useMemo(() => {
    return subTasks.filter((t) => {
      const title = (t.Title || t.title || "").toLowerCase();
      const search = (titleSearch || "").trim().toLowerCase();
      if (search && !title.includes(search)) return false;
      return true;
    });
  }, [subTasks, titleSearch]);

  const getSubTaskSortValue = (subTask, columnId) => {
    const rawDate = (value) => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };
    switch (columnId) {
      case "title":
        return String(subTask?.Title ?? subTask?.title ?? "").toLowerCase();
      case "description":
        return String(subTask?.Description ?? subTask?.description ?? "").toLowerCase();
      case "priority":
        return String(subTask?.Priority ?? subTask?.priority ?? "").toLowerCase();
      case "status":
        return String(subTask?.Status ?? subTask?.status ?? "").toLowerCase();
      case "startDate":
        return rawDate(subTask?.Start_Date ?? subTask?.start_date);
      case "endDate":
        return rawDate(subTask?.End_Date ?? subTask?.end_date);
      case "createdBy":
        return String(subTask?.Created_By_Name ?? subTask?.created_by_name ?? "").toLowerCase();
      case "createdOn":
        return rawDate(subTask?.Created_On ?? subTask?.created_on);
      default:
        return "";
    }
  };

  const sortedSubTasks = useMemo(() => {
    if (!sortColumn) return filteredSubTasks;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...filteredSubTasks].sort((a, b) => {
      const va = getSubTaskSortValue(a, sortColumn);
      const vb = getSubTaskSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.Sub_Task_Id ?? a?.sub_task_id ?? 0);
      const idb = Number(b?.Sub_Task_Id ?? b?.sub_task_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [filteredSubTasks, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedSubTasks.length / rowsPerPage));
  const startIdx = (subTaskPage - 1) * rowsPerPage;
  const currentSubTasks = sortedSubTasks.slice(startIdx, startIdx + rowsPerPage);

  useEffect(() => {
    if (subTaskPage > totalPages && totalPages > 0) {
      setSubTaskPage(1);
    }
  }, [subTaskPage, totalPages]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setSubTaskPage(1);
    localStorage.setItem(SUBTASKS_ROWS_PER_PAGE_KEY, String(next));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const handleDeleteSubTask = async (subTaskId) => {
    if (!window.confirm("Are you sure you want to delete this subtask?")) return;
    setDeletingId(subTaskId);
    try {
      await deleteSubTask(subTaskId);
      setSubTasks((prev) => prev.filter((t) => (t.Sub_Task_Id ?? t.sub_task_id) !== subTaskId));
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Error deleting subtask:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to delete subtask.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddSubTaskClick = () => {
    const addPath = subProjectId
      ? `/projects/${projectId}/subprojects/${subProjectId}/user-stories/${storyId}/tasks/${taskId}/subtasks/add`
      : `/projects/${projectId}/user-stories/${storyId}/tasks/${taskId}/subtasks/add`;
    navigate(addPath);
  };

  const handleSort = (columnId) => {
    setSubTaskPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const contextLabel = subProjectName ? `${projectName} / ${subProjectName}` : projectName;

  const statusOptions = useMemo(() => {
    const map = new Map();
    subTasks.forEach((t) => {
      const id = t.Status_Id ?? t.status_id;
      const label = t.Status || t.status;
      if (id != null && label && label !== "—") map.set(id, label);
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ value: String(id), label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [subTasks]);

  const getViewPath = (subTaskId) =>
    subProjectId
      ? `/projects/${projectId}/subprojects/${subProjectId}/user-stories/${storyId}/tasks/${taskId}/subtasks/${subTaskId}/view`
      : `/projects/${projectId}/user-stories/${storyId}/tasks/${taskId}/subtasks/${subTaskId}/view`;

  const getEditPath = (subTaskId) =>
    subProjectId
      ? `/projects/${projectId}/subprojects/${subProjectId}/user-stories/${storyId}/tasks/${taskId}/subtasks/${subTaskId}/edit`
      : `/projects/${projectId}/user-stories/${storyId}/tasks/${taskId}/subtasks/${subTaskId}/edit`;

  return (
    <div className="viewlead-card project-subprojects-view-card tasks-table-card subtasks-table-card">
      <div className="tasks-header userstories-header">
        <h3 className="viewlead-section-title">
          Subtasks
          {taskTitle && (
            <span className="userstories-context-label">({taskTitle})</span>
          )}
          {contextLabel && (
            <span className="userstories-context-label">{contextLabel}</span>
          )}
        </h3>
        <button
          type="button"
          className="btn-subproject-primary"
          onClick={handleAddSubTaskClick}
        >
          Add Subtask
        </button>
      </div>

      {!loading && (
        <div className="userstories-filters-row">
          <input
            type="text"
            className="userstories-title-search"
            placeholder="Search by title..."
            value={titleSearch}
            onChange={(e) => {
              setTitleSearch(e.target.value);
              setSubTaskPage(1);
            }}
          />
          <select
            className="userstories-status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSubTaskPage(1);
            }}
          >
            <option value="">All Status</option>
            {statusOptions.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            className="userstories-priority-filter userstories-status-filter"
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setSubTaskPage(1);
            }}
          >
            <option value="">All Priority</option>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              setTitleSearch("");
              setStatusFilter("");
              setPriorityFilter("");
              setSubTaskPage(1);
            }}
            style={{ marginLeft: 8 }}
          >
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="project-subprojects-loading">Loading subtasks...</div>
      ) : (
        <>
          <div className="projects-container tasks-table-container userstories-table-container">
            <div className="table-wrapper userstories-table-wrapper">
              <div className="table-container">
                <table className="data-table projects-table-frozen">
                  <thead>
                    <tr>
                      <th className="col-sticky col-0 projects-th-sortable" {...getColProps(0)}>
                        <div className="projects-th-sort-row">
                          <SupportSortableHeader columnId="title" label="Title" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={0} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(1)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="description" label="Description" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={1} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(2)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="priority" label="Priority" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={2} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(3)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={3} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(4)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="startDate" label="Start Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={4} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(5)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="endDate" label="End Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={5} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(6)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="createdBy" label="Created By" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={6} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(7)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="createdOn" label="Created On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={7} />
                      </th>
                      <th className="col-sticky col-action" {...getColProps(8)}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSubTasks.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="userstories-empty-cell">
                          No subtasks yet.
                        </td>
                      </tr>
                    ) : (
                      currentSubTasks.map((subTask) => {
                        const id = subTask.Sub_Task_Id ?? subTask.sub_task_id;
                        return (
                          <tr key={id}>
                            <td className="col-sticky col-0">
                              <span
                                className="table-name-link"
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(getViewPath(id))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    navigate(getViewPath(id));
                                  }
                                }}
                              >
                                <strong>{subTask.Title || subTask.title || "—"}</strong>
                              </span>
                            </td>
                            <td style={{ textAlign: "left", maxWidth: 300 }}>
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 300
                                }}
                                title={subTask.Description || subTask.description || ""}
                              >
                                {subTask.Description || subTask.description || "—"}
                              </span>
                            </td>
                            <td>
                              {(() => {
                                const priority = subTask.Priority ?? subTask.priority ?? "";
                                const priorityClass = priority
                                  ? `status-${String(priority).toLowerCase().replace(/\s/g, "-")}`
                                  : "status-not-set";
                                return (
                                  <span className={`status-badge ${priorityClass}`}>
                                    {priority || "Not Set"}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <span
                                className={`status-badge status-${(
                                  subTask.Status ||
                                  subTask.status ||
                                  ""
                                )
                                  .toLowerCase()
                                  .replace(/\s/g, "-")}`}
                              >
                                {subTask.Status || subTask.status || "—"}
                              </span>
                            </td>
                            <td>{formatDate(subTask.Start_Date || subTask.start_date)}</td>
                            <td>{formatDate(subTask.End_Date || subTask.end_date)}</td>
                            <td>{subTask.Created_By_Name || subTask.created_by_name || "—"}</td>
                            <td>{formatDate(subTask.Created_On || subTask.created_on)}</td>
                            <td className="col-sticky col-action">
                              <div className="project-actions-cell">
                                <button
                                  type="button"
                                  className="project-action-btn project-action-btn-icon project-action-btn-edit"
                                  title="Edit subtask"
                                  onClick={() => navigate(getEditPath(id))}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  type="button"
                                  className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                                  title="Delete subtask"
                                  disabled={deletingId === id}
                                  onClick={() => handleDeleteSubTask(id)}
                                >
                                  <FaTrashAlt />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {!loading && sortedSubTasks.length > 0 ? (
              <TablePagination
                currentPage={subTaskPage}
                totalItems={sortedSubTasks.length}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                onPageChange={setSubTaskPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                itemLabel="subtasks"
                rowsPerPageSelectId="subtasks-rows-per-page"
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export default SubTasksTable;

